import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from './translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Helper function to detect language from URL path
export const getLanguageFromPath = (pathname) => {
  const langMatch = pathname.match(/^\/(fr|en|es|de)(\/|$)/);
  return langMatch ? langMatch[1] : 'fr'; // default to French
};

// Helper function to remove language prefix from path
export const removeLanguagePrefix = (pathname) => {
  return pathname.replace(/^\/(fr|en|es|de)/, '') || '/';
};

// Helper function to add language prefix to path
export const addLanguagePrefix = (pathname, lang) => {
  const cleanPath = removeLanguagePrefix(pathname);
  return `/${lang}${cleanPath}`;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [language, setLanguage] = useState(() => {
    // First, try to get language from URL
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang) return urlLang;
    
    // Fallback to localStorage or default
    return localStorage.getItem('language') || 'fr';
  });

  // Update language when URL changes
  useEffect(() => {
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang !== language) {
      setLanguage(urlLang);
      localStorage.setItem('language', urlLang);
      document.documentElement.lang = urlLang;
    }
  }, [location.pathname, language]);

  // Update document language
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Function to change language with navigation
  const changeLanguage = (newLang) => {
    const currentPath = removeLanguagePrefix(location.pathname);
    const newPath = addLanguagePrefix(currentPath, newLang);
    navigate(newPath);
    setLanguage(newLang);
  };

  const t = (path) => {
    const keys = path.split('.');
    let value = translations[language];
    
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) {
        console.warn(`Translation missing for: ${path} in ${language}`);
        return path;
      }
    }
    
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
